import { type EventStandardDefs } from '@solinkcorp/event-standard';
import { type IEvent } from 'clients/PlatformApi';
import get from 'lodash/get';
import { RECEIPT_LINE_HEIGHT } from 'vd-constants';
import { BaseFieldRenderer } from './base';
import { PrintText } from './printRows';
import { type FieldRendererField, type FieldValue } from './types';

/** CSS class name for skill chip — defined in Event.sass */
export const SKILL_CHIP_CLASS = 'skill-match-chip';

/**
 * Renders skillMatches as inline chip/tag elements.
 * Used for details.skillMatches on agent event cards.
 */
// Assume ~3 chips fit per row on a typical event card width
const CHIPS_PER_ROW = 3;
// Each row of chips is roughly 2 receipt lines tall (chip height + gap + padding)
const ROW_HEIGHT = RECEIPT_LINE_HEIGHT * 2;

export class SkillMatchesFieldRenderer extends BaseFieldRenderer {
  static computeHeight({
    event,
    field,
  }: {
    eventStandard: EventStandardDefs;
    event: IEvent;
    field: FieldRendererField;
    fieldValue: FieldValue;
  }): number {
    const skills = get(event, field.name) as string[] | undefined;
    if (!Array.isArray(skills) || skills.length === 0) return 0;

    const rows = Math.ceil(skills.length / CHIPS_PER_ROW);
    return rows * ROW_HEIGHT;
  }

  render() {
    const { fieldValue } = this.props;
    const skills = Array.isArray(fieldValue) ? fieldValue : [];

    if (skills.length === 0) return null;

    if (this.props.variant === 'print') {
      return (
        <PrintText
          data-capsule-name={this.getCapsuleName()}
          data-testid={this.getCapsuleTestId()}
          onContextMenu={this.onContextMenu}
        >
          {`${this.i18nFieldName()}: ${skills.join(', ')}`}
        </PrintText>
      );
    }

    return (
      <div
        data-capsule-name={this.getCapsuleName()}
        data-testid={this.getCapsuleTestId()}
        onContextMenu={this.onContextMenu}
        style={{ ...this.style(), display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 0' }}
        ref={(node) => {
          this.node = node || undefined;
        }}
      >
        {skills.map((skill: string) => (
          <span key={skill} className={SKILL_CHIP_CLASS}>
            {skill}
          </span>
        ))}
      </div>
    );
  }
}
